import React, { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/tables/DataTable";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SelectField } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Edit,
  Trash2,
  User as UserIcon,
  Download,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { User, apiService } from "@/services/api";
import { UserRole, UserStatus, getRoleDisplayName, getUserStatusDisplayName } from "@/constants/enums";

const getStatusBadge = (status: number) => {
  const displayName = getUserStatusDisplayName(status);
  switch (status) {
    case UserStatus.VERIFIED:
      return <Badge className="bg-green-100 text-green-800">{displayName}</Badge>;
    case UserStatus.PENDING_VERIFICATION:
      return <Badge className="bg-yellow-100 text-yellow-800">{displayName}</Badge>;
    case UserStatus.CREATED:
      return <Badge className="bg-blue-100 text-blue-800">{displayName}</Badge>;
    case UserStatus.BLOCKED:
      return <Badge className="bg-red-100 text-red-800">{displayName}</Badge>;
    case UserStatus.DELETED:
      return <Badge variant="secondary">{displayName}</Badge>;
    default:
      return <Badge variant="outline">{displayName}</Badge>;
  }
};

const getRoleBadge = (role: number) => {
  const displayName = getRoleDisplayName(role);
  switch (role) {
    case UserRole.ADMIN:
      return <Badge className="bg-red-100 text-red-800">{displayName}</Badge>;
    case UserRole.MANAGER:
      return <Badge className="bg-blue-100 text-blue-800">{displayName}</Badge>;
    case UserRole.STAFF:
      return <Badge className="bg-green-100 text-green-800">{displayName}</Badge>;
    case UserRole.ESTABLISHMENT:
      return <Badge className="bg-purple-100 text-purple-800">{displayName}</Badge>;
    default:
      return <Badge variant="outline">{displayName}</Badge>;
  }
};

export function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Bulk operations state
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isBulkActionDialogOpen, setIsBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [filters, setFilters] = useState<{
    role: number | "";
    status: string;
    dateRange: string;
  }>({
    role: "",
    status: "",
    dateRange: "",
  });

  // Edit user state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState({
    fullName: "",
    email: "",
    role: 0,
    userStatus: 0,
  });

  // Helper function to convert dateRange to actual dates
  const getDateRangeValues = (range: string): { startDate?: string; endDate?: string } => {
    if (!range) return {};
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (range) {
      case "today": {
        return { 
          startDate: today.toISOString(), 
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString() 
        };
      }
      case "week": {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return { 
          startDate: weekStart.toISOString(), 
          endDate: now.toISOString() 
        };
      }
      case "month": {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return { 
          startDate: monthStart.toISOString(), 
          endDate: now.toISOString() 
        };
      }
      case "year": {
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return { 
          startDate: yearStart.toISOString(), 
          endDate: now.toISOString() 
        };
      }
      default:
        return {};
    }
  };

  // Load users from API with filters
  const loadUsers = React.useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRangeValues(filters.dateRange);
      
      const response = await apiService.getAllUsers({
        page,
        role: filters.role !== "" ? filters.role : undefined,
        status: filters.status || undefined,
        startDate,
        endDate,
      });
      if (response.type === 0) {
        const data = response.data as {
          history: User[];
          page: number;
          total_page: number;
          total_count: number;
        };
        setUsers(data.history);
        setCurrentPage(data.page);
        setTotalPages(data.total_page);
        setTotalCount(data.total_count);
      } else {
        toast.error(response.message || "Failed to load users");
      }
    } catch (error) {
      toast.error("Failed to load users");
      console.error("Error loading users:", error);
    } finally {
      setLoading(false);
    }
  }, [filters.role, filters.status, filters.dateRange]);

  // Load users on component mount
  React.useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  // Delete user
  const handleDeleteUser = async (userId: string) => {
    if (!confirm("Are you sure you want to delete this user?")) return;

    try {
      const response = await apiService.deleteUser(userId);
      if (response.type === 0) {
        toast.success("User deleted successfully");
        void loadUsers(currentPage);
      } else {
        toast.error(response.message || "Failed to delete user");
      }
    } catch (error) {
      toast.error("Failed to delete user");
      console.error("Error deleting user:", error);
    }
  };

  // Open edit dialog
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      fullName: user.fullName,
      email: user.email,
      role: user.role,
      userStatus: user.userStatus,
    });
    setIsEditDialogOpen(true);
  };

  // Save edited user
  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    try {
      const response = await apiService.updateUser(selectedUser._id, editFormData);
      if (response.type === 0) {
        toast.success("User updated successfully");
        setIsEditDialogOpen(false);
        setSelectedUser(null);
        void loadUsers(currentPage);
      } else {
        toast.error(response.message || "Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
      console.error("Error updating user:", error);
    }
  };

  // Bulk operations handlers
  // const handleSelectAll = (checked: boolean) => {
  //   if (checked) {
  //     setSelectedUsers(users.map(user => user._id));
  //   } else {
  //     setSelectedUsers([]);
  //   }
  // };

  // const handleSelectUser = (userId: string, checked: boolean) => {
  //   if (checked) {
  //     setSelectedUsers(prev => [...prev, userId]);
  //   } else {
  //     setSelectedUsers(prev => prev.filter(id => id !== userId));
  //   }
  // };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) return;

    try {
      switch (bulkAction) {
        case "delete":
          // Delete users one by one
          for (const userId of selectedUsers) {
            await apiService.deleteUser(userId);
          }
          toast.success(`${selectedUsers.length} users deleted successfully`);
          void loadUsers(currentPage);
          break;
        case "export":
          exportUsers(selectedUsers);
          break;
        default:
          toast.error("Invalid bulk action");
          return;
      }

      setSelectedUsers([]);
      setIsBulkActionDialogOpen(false);
      setBulkAction("");
    } catch (error) {
      toast.error("Failed to perform bulk action");
      console.error("Error in bulk action:", error);
    }
  };

  const exportUsers = (userIds: string[]) => {
    const usersToExport = users.filter(user => userIds.includes(user._id));
    const csvContent = [
      ["ID", "Full Name", "Email", "Role", "Status", "Created At"],
      ...usersToExport.map(user => [
        user._id,
        user.fullName,
        user.email,
        getRoleDisplayName(user.role),
        user.userStatus === 1 ? "Active" : "Inactive",
        format(new Date(user.createdAt), "yyyy-MM-dd"),
      ]),
    ]
      .map(row => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success("Users exported successfully");
  };

  const handleImportUsers = async () => {
    if (!importFile) return;

    try {
      // Simulate file processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success("Users imported successfully");
      setIsImportDialogOpen(false);
      setImportFile(null);
    } catch {
      toast.error("Failed to import users");
    }
  };

  // Users are now filtered on the backend, no need for local filtering

  // Define columns inside component to access state
  const columns: ColumnDef<User>[] = [
    // {
    //   id: "select",
    //   header: ({ table }) => (
    //     <Checkbox
    //       checked={table.getIsAllPageRowsSelected()}
    //       onCheckedChange={value => {
    //         handleSelectAll(value as boolean);
    //       }}
    //       aria-label="Select all"
    //     />
    //   ),
    //   cell: ({ row }) => (
    //     <Checkbox
    //       checked={selectedUsers.includes(row.original._id)}
    //       onCheckedChange={value => {
    //         handleSelectUser(row.original._id, value as boolean);
    //       }}
    //       aria-label="Select row"
    //     />
    //   ),
    //   enableSorting: false,
    //   enableHiding: false,
    // },
    {
      accessorKey: "fullName",
      header: "User",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex items-center space-x-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.profilePicture?.fileUrl} alt={user.fullName} />
              <AvatarFallback>
                {user.fullName
                  .split(" ")
                  .map(n => n[0])
                  .join("")}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">{user.fullName}</div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => getRoleBadge(row.original.role),
    },
    {
      accessorKey: "userStatus",
      header: "Status",
      cell: ({ row }) => getStatusBadge(row.original.userStatus),
    },
    {
      accessorKey: "createdAt",
      header: "Joined",
      cell: ({ row }) => format(new Date(row.original.createdAt), "MMM dd, yyyy"),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="items-center justify-start gap-1">
            <Button
              variant="ghost"
              size="sm"
              title="View Profile"
              onClick={() => {
                navigate(`/users/${user._id}`);
              }}
            >
              <UserIcon className="mr-2 h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              title="Edit User"
              onClick={() => {
                handleEditUser(user);
              }}
            >
              <Edit className="mr-2 h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              title="Delete User"
              className="text-red-600 hover:text-red-700"
              onClick={() => {
                void handleDeleteUser(user._id);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground">Manage all users in the SnapShift platform</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => {
              void loadUsers(currentPage);
            }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {/* <Button
            variant="outline"
            onClick={() => {
              setIsImportDialogOpen(true);
            }}
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Users
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              exportUsers(users.map(u => u._id));
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            Export All
          </Button> */}
        </div>
      </div>

      {/* Filters and Bulk Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Filters & Bulk Operations</span>
            {selectedUsers.length > 0 && (
              <Badge variant="secondary">{selectedUsers.length} selected</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="role-filter">Role</Label>
              <SelectField
                value={filters.role === "" ? "" : String(filters.role)}
                onValueChange={(value: string) => {
                  setFilters(prev => ({ 
                    ...prev, 
                    role: value === "" ? "" : parseInt(value, 10) 
                  }));
                }}
                placeholder="All Roles"
                options={[
                  { label: "All Roles", value: "" },
                  { label: "Admin", value: String(UserRole.ADMIN) },
                  { label: "Manager", value: String(UserRole.MANAGER) },
                  { label: "Staff", value: String(UserRole.STAFF) },
                  { label: "Establishment", value: String(UserRole.ESTABLISHMENT) },
                ]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status-filter">Status</Label>
              <SelectField
                value={filters.status}
                onValueChange={(value: string) => {
                  setFilters(prev => ({ ...prev, status: value }));
                }}
                placeholder="All Status"
                options={[
                  { label: "All Status", value: "" },
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date-filter">Date Range</Label>
              <SelectField
                value={filters.dateRange}
                onValueChange={(value: string) => {
                  setFilters(prev => ({ ...prev, dateRange: value }));
                }}
                placeholder="All Time"
                options={[
                  { label: "All Time", value: "" },
                  { label: "Today", value: "today" },
                  { label: "This Week", value: "week" },
                  { label: "This Month", value: "month" },
                  { label: "This Year", value: "year" },
                ]}
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setFilters({ role: "", status: "", dateRange: "" });
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedUsers.length > 0 && (
            <div className="flex items-center space-x-2 pt-4 border-t">
              <span className="text-sm font-medium">Bulk Actions:</span>
              {/* <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBulkAction("activate");
                  setIsBulkActionDialogOpen(true);
                }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Activate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBulkAction("suspend");
                  setIsBulkActionDialogOpen(true);
                }}
              >
                <Clock className="w-4 h-4 mr-2" />
                Suspend
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setBulkAction("delete");
                  setIsBulkActionDialogOpen(true);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button> */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBulkAction("export");
                  setIsBulkActionDialogOpen(true);
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Export Selected
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={users}
        searchKey="fullName"
        searchPlaceholder="Search users..."
        pageSize={12}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * 12 + 1} to {Math.min(currentPage * 12, totalCount)} of{" "}
            {totalCount} results
          </p>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void loadUsers(currentPage - 1);
              }}
              disabled={currentPage === 1 || loading}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void loadUsers(currentPage + 1);
              }}
              disabled={currentPage === totalPages || loading}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={isBulkActionDialogOpen} onOpenChange={setIsBulkActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Action</DialogTitle>
            <DialogDescription>
              Are you sure you want to {bulkAction} {selectedUsers.length} selected user
              {selectedUsers.length > 1 ? "s" : ""}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsBulkActionDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleBulkAction}>Confirm {bulkAction}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Users Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Users</DialogTitle>
            <DialogDescription>
              Upload a CSV file with user data. The file should include columns for: Full Name,
              Email, Role, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="import-file">CSV File</Label>
              <Input
                id="import-file"
                type="file"
                accept=".csv"
                onChange={e => {
                  setImportFile(e.target.files?.[0] || null);
                }}
              />
            </div>
            <div className="text-sm text-muted-foreground">
              <p>
                Download the{" "}
                <Button
                  variant="link"
                  className="p-0 h-auto"
                  onClick={() => {
                    exportUsers([]);
                  }}
                >
                  template
                </Button>{" "}
                to see the required format.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsImportDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleImportUsers} disabled={!importFile}>
              Import Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information. Changes will be saved immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-fullName">Full Name</Label>
              <Input
                id="edit-fullName"
                value={editFormData.fullName}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, fullName: e.target.value })
                }
                placeholder="Enter full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, email: e.target.value })
                }
                placeholder="Enter email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <SelectField
                value={String(editFormData.role)}
                onValueChange={(value: string) =>
                  setEditFormData({ ...editFormData, role: parseInt(value, 10) })
                }
                placeholder="Select role"
                options={[
                  { label: "Admin", value: String(UserRole.ADMIN) },
                  { label: "Manager", value: String(UserRole.MANAGER) },
                  { label: "Staff", value: String(UserRole.STAFF) },
                  { label: "Establishment", value: String(UserRole.ESTABLISHMENT) },
                ]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <SelectField
                value={String(editFormData.userStatus)}
                onValueChange={(value: string) =>
                  setEditFormData({ ...editFormData, userStatus: parseInt(value, 10) })
                }
                placeholder="Select status"
                options={[
                  { label: "Created", value: String(UserStatus.CREATED) },
                  { label: "Pending Verification", value: String(UserStatus.PENDING_VERIFICATION) },
                  { label: "Verified", value: String(UserStatus.VERIFIED) },
                  { label: "Blocked", value: String(UserStatus.BLOCKED) },
                ]}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
